import { Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import notify from '@/lib/notify';
import { User } from '../../../types';

import { Transition } from '@headlessui/react';
// Interface des props, adaptée pour le Cadrage
interface CadrageTeamSectionProps {
  teamMembers: User[] | undefined; // Membres de l'équipe du cadrage
  availableUsers: User[]; // Utilisateurs disponibles pour l'ajout
  onAddMember: (userId: string) => Promise<void>; // Fonction pour ajouter
  onRemoveMember: (userId: string) => Promise<void>; // Fonction pour supprimer
  isLoading?: boolean; // État de chargement des opérations
  isReadOnly?: boolean; // Mode lecture seule
}

const CadrageTeamSection: React.FC<CadrageTeamSectionProps> = ({
  teamMembers,
  availableUsers,
  onAddMember,
  onRemoveMember,
  isLoading = false,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedUserIdToAdd, setSelectedUserIdToAdd] = useState('');
  const [userSearch, setUserSearch] = useState('');

  // Filtrer les utilisateurs disponibles pour ne pas inclure ceux déjà dans l'équipe
  const usersNotInTeam = useMemo(() => (
    availableUsers.filter(
      (availUser) => !teamMembers?.some((teamMember) => teamMember.id === availUser.id)
    )
  ), [availableUsers, teamMembers]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return usersNotInTeam;
    return usersNotInTeam.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [userSearch, usersNotInTeam]);

  const handleAddClick = async () => {
    if (!selectedUserIdToAdd) return;
    try {
      await onAddMember(selectedUserIdToAdd);
      setShowAddMemberModal(false);
      setSelectedUserIdToAdd('');
    } catch (error) {
      console.error("Erreur lors de l'ajout du membre au cadrage:", error);
      notify.error(t('cadrageDetail.addMemberError'));
    }
  };

  const handleRemoveClick = async (userId: string, userName: string) => {
    if (!confirm(t('cadrageDetail.removeFromCadrage', { name: userName }))) return;
    try {
      await onRemoveMember(userId);
    } catch (error) {
      console.error("Erreur lors de la suppression du membre du cadrage:", error);
      notify.error(t('cadrageDetail.removeMemberError'));
    }
  };

  return (
    <>
      <div className="bg-card rounded-lg shadow p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">{t('cadrageDetail.cadrageTeam')}</h2>
          {!isReadOnly && (
            <button
              onClick={() => setShowAddMemberModal(true)}
              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90"
              disabled={isLoading}
            >
              <Plus className="h-4 w-4 mr-1" /> {t('cadrageDetail.add')}
            </button>
          )}
        </div>

        {teamMembers?.length ? (
          <ul className="space-y-3">
            {teamMembers.map((m) => (
              <li key={m.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <img
                    src={m.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=random`}
                    alt={m.name}
                    className="h-8 w-8 rounded-full"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">{m.name}</p>
                    {m.role && <p className="text-sm text-muted-foreground">{m.role}</p>}
                  </div>
                </div>
                {!isReadOnly && (
                  <button
                    onClick={() => handleRemoveClick(m.id, m.name)}
                    className="text-muted-foreground hover:text-red-500"
                    disabled={isLoading}
                    title={t('settings.delete') + ' ' + m.name}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground italic">{t('cadrageDetail.noMembers')}</p>
        )}
      </div>

      <Transition appear show={showAddMemberModal} as={React.Fragment}>
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md p-6 space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-foreground">{t('cadrageDetail.addMember')}</h3>
              <button
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setShowAddMemberModal(false)}
              >
                &times;
              </button>
            </div>

            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder={t('cadrageDetail.searchByNameEmail')}
              className="block w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
            />

            <select
              value={selectedUserIdToAdd}
              onChange={(e) => setSelectedUserIdToAdd(e.target.value)}
              className="block w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground focus:outline-none focus:ring-ring focus:border-ring sm:text-sm"
            >
              <option value="">{t('cadrageDetail.selectUser')}</option>
              {/* Utiliser la liste filtrée */}
              {filteredUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                  {u.role ? ` • ${u.role}` : ''}
                  {u.email && u.name ? ` • ${u.email}` : ''}
                </option>
              ))}
            </select>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowAddMemberModal(false)}
                className="px-4 py-2 border border-border rounded-md shadow-sm text-sm font-medium text-foreground bg-background hover:bg-accent"
                disabled={isLoading}
              >
                {t('cadrageDetail.cancel')}
              </button>
              <button
                onClick={handleAddClick}
                disabled={!selectedUserIdToAdd || isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 disabled:opacity-50"
              >
                {isLoading ? t('cadrageDetail.adding') : t('cadrageDetail.add')}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
};

export default CadrageTeamSection;
